// Connection to the venue's live-score service: reconnects by itself, keeps a server-time offset
// so every screen shows the same clock, and turns command replies into promises.
import type { Command, MatchState } from '../shared/match';
import type { ServerMsg } from '../shared/protocol';
import type { Club } from '../shared/club';

export type Status = 'connecting' | 'live' | 'offline' | 'denied' | 'ended' | 'handedover';

export interface Link {
  send(cmd: Command): Promise<void>;
  serverNow(): number;
  status(): Status;
  /** Ask for a one-time code another phone can scan to take over scoring. */
  handover(): Promise<{ code: string; expiresAt: number }>;
  cancelHandover(): void;
}

export function connect(venue: string, o: {
  role: 'viewer' | 'scorer'; pin?: string;
  onState(state: MatchState | null): void;
  onStatus?(s: Status, detail?: string): void;
  onClub?(): void;
}): Link {
  let ws: WebSocket | null = null, status: Status = 'connecting', offset = 0, nextId = 1, backoff = 1000;
  let retryTimer = 0, lastHeard = Date.now(), stopped = false;
  const pending = new Map<number, { ok(m: ServerMsg): void; fail(e: Error): void; timer: number }>();
  const q = new URLSearchParams({ role: o.role, ...(o.pin ? { pin: o.pin } : {}) });
  const path = `/api/venue/${encodeURIComponent(venue)}/ws?${q}`;
  const set = (s: Status, d?: string) => { status = s; o.onStatus?.(s, d); };

  async function open() {
    set('connecting');
    if (o.role === 'scorer') {
      // check the link before opening a socket, so an old PIN gets a clear message
      try {
        const r = await fetch(path);
        if (r.status === 403 || r.status === 429) { stopped = true; return set('denied', await r.text()); }
      } catch { return retry(); }
    }
    ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${path}`);
    ws.onopen = () => { backoff = 1000; set('live'); ping(); };
    ws.onmessage = ev => {
      lastHeard = Date.now();
      const m = JSON.parse(ev.data) as ServerMsg;
      if (m.type === 'state') { offset = m.now - Date.now(); o.onState(m.state); }
      else if (m.type === 'pong') { const rtt = Date.now() - m.t; offset = m.now + rtt / 2 - Date.now(); }
      else if (m.type === 'club') o.onClub?.();
      else if (m.type === 'ended') { stopped = true; set(m.handedOver ? 'handedover' : 'ended', m.reason); try { ws?.close(); } catch { /* closing */ } }
      else if (m.type === 'ack' || m.type === 'error' || m.type === 'handover') {
        const p = m.id != null ? pending.get(m.id) : undefined;
        if (p) { clearTimeout(p.timer); pending.delete(m.id as number); m.type === 'error' ? p.fail(new Error(m.message)) : p.ok(m); }
      }
    };
    ws.onclose = ev => {
      for (const [id, p] of pending) { clearTimeout(p.timer); p.fail(new Error('Connection lost, so that was not sent. Try again.')); pending.delete(id); }
      if (stopped) return;                         // already told the match is over
      if (ev.code === 4001) { stopped = true; return set('ended', ev.reason); }
      if (ev.code === 4002) { stopped = true; return set('handedover', ev.reason); }
      retry();
    };
  }
  function retry() { set('offline'); clearTimeout(retryTimer); retryTimer = window.setTimeout(open, backoff); backoff = Math.min(backoff * 2, 8000); }
  function ping() { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping', t: Date.now() })); }
  setInterval(ping, 20_000);
  /** The phone woke up or got signal back: reconnect now instead of waiting, and replace a socket that died while asleep. */
  function wakeUp() {
    if (stopped || document.hidden) return;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) { clearTimeout(retryTimer); backoff = 1000; open(); return; }
    if (ws.readyState === WebSocket.OPEN) {
      const asked = Date.now();
      ping();
      setTimeout(() => { if (lastHeard < asked && ws?.readyState === WebSocket.OPEN) ws.close(); }, 4000);   // no answer: it's a dead socket
    }
  }
  document.addEventListener('visibilitychange', wakeUp);
  addEventListener('online', wakeUp);
  addEventListener('pageshow', wakeUp);
  open();

  /** Send a message that expects a reply, and wait for it. */
  function ask(msg: (id: number) => object, noReply: string) {
    return new Promise<ServerMsg>((ok, fail) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return fail(new Error('Not connected, so that was not sent. Check your signal and try again.'));
      const id = nextId++;
      const timer = window.setTimeout(() => { pending.delete(id); fail(new Error(noReply)); }, 6000);
      pending.set(id, { ok, fail, timer });
      ws.send(JSON.stringify(msg(id)));
    });
  }

  return {
    serverNow: () => Date.now() + offset,
    status: () => status,
    async send(cmd) { await ask(id => ({ type: 'cmd', id, cmd }), 'No reply from the screen service. Check the score before trying again.'); },
    async handover() {
      const m = await ask(id => ({ type: 'handover', id }), 'No reply from the screen service. Try again.');
      if (m.type !== 'handover') throw new Error('That did not work. Try again.');
      return { code: m.code, expiresAt: m.expiresAt };
    },
    cancelHandover() { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'handoverCancel' })); },
  };
}

export type { Club, Player, Squad, Sponsor } from '../shared/club';

/** The club's details: what Studio saved, or the starter file shipped with the app if nothing is saved yet. */
export async function loadClub(venue: string): Promise<Club> {
  const v = encodeURIComponent(venue);
  let credit: Club['credit'];
  try {
    const r = await fetch(`/api/venue/${v}/club`, { cache: 'no-store' });
    if (r.ok) {
      const saved = await r.json() as Club;
      if (saved.league === undefined) {
        // saved before Studio had a league list: borrow the starter list until Studio is next saved
        try { const st = await fetch(`/clubs/${v}/club.json`); if (st.ok) saved.league = ((await st.json()) as Club).league ?? []; } catch { saved.league = []; }
      }
      return saved;
    }
    credit = ((await r.json().catch(() => ({}))) as { credit?: Club['credit'] }).credit;   // nothing saved yet: the Matchlight credit still comes from the service
  } catch { /* fall back to the starter file */ }
  const r = await fetch(`/clubs/${v}/club.json`);
  if (!r.ok) throw new Error(`No club set up for "${venue}"`);
  return { ...(await r.json()), ...(credit ? { credit } : {}) };
}

/** Players in shirt-number order, then by name; unnumbered players last. */
export const byNumber = <T extends { number: string; name: string }>(ps: T[]) =>
  [...ps].sort((a, b) => (Number(a.number) || 999) - (Number(b.number) || 999) || a.name.localeCompare(b.name));

export const esc = (t: unknown) => String(t ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
export function contrast(hex: string) {
  const n = parseInt(hex.slice(1), 16), r = n >> 16, g = (n >> 8) & 255, b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#000' : '#fff';
}
export const param = (k: string, d = '') => new URLSearchParams(location.search).get(k) ?? d;
/** The club this address belongs to: wensleydale.matchlight.uk → "wensleydale". */
export function hostVenue(): string | null {
  const m = location.hostname.match(/^([a-z0-9-]+)\.matchlight\.uk$/);
  return m && m[1] !== 'www' ? m[1] : null;
}
/** Which club's screen this page is for: ?venue= if given, else the club's own address, else Wensleydale (the old shared address). */
export const venueParam = () => param('venue') || hostVenue() || 'wensleydale';

export { readableOnBlack } from '../shared/colors';

/** The club's own banner on the desktop pages: its crest beside its name, and a line in the club colour. */
export function brandHeader(club: Pick<Club, 'name' | 'crest' | 'color'>) {
  const el = document.getElementById('clubName');
  if (!el) return;
  el.innerHTML = `${club.crest ? `<img class="clubcrest" src="${esc(club.crest)}" alt="">` : ''}<span>${esc(club.name)}</span>`;
  const header = el.closest('header');
  if (header && /^#[0-9a-f]{6}$/i.test(club.color)) header.style.borderBottomColor = club.color;
}

